import useCollabReferentials from "../hooks/useCollabReferentials";
import CollaboratorPicker from "./CollaboratorPicker";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const NONE = "__none__";
const today = () => new Date().toISOString().slice(0, 10);

export function defaultFicheDraft() {
  return { relation_type: "", job_title: "", status: "active", entry_date: today(), service_id: "", manager_id: "", manager_user_id: "", manager_label: "", phone: "" };
}

export function isFicheDraftValid(draft, relationFallback) {
  const type = draft.relation_type || relationFallback;
  return !!type && (draft.status !== "active" || (!!draft.entry_date && draft.entry_date <= today()));
}

// Extra fields shown when "create a collaborator fiche too" is on (user creation page). Email and WhatsApp come from the account form.
export default function FicheFieldsForUser({ draft, onChange }) {
  const refs = useCollabReferentials();
  const types = refs.relation_types.filter((r) => r.active);
  const services = refs.services.filter((s) => s.active);
  const type = draft.relation_type || (types.find((r) => r.code === "salarie") || types[0])?.code || "";
  const set = (patch) => onChange({ ...draft, ...patch });
  return (
    <div className="space-y-4 border-t border-slate-100 pt-4" data-testid="fiche-fields">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Type de relation *</Label>
          <Select value={type} onValueChange={(v) => set({ relation_type: v })} disabled={refs.status !== "ready"}>
            <SelectTrigger data-testid="fiche-type"><SelectValue placeholder="Choisir" /></SelectTrigger>
            <SelectContent>{types.map((r) => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="fiche-job-title">Fonction</Label>
          <Input id="fiche-job-title" className="text-base md:text-sm" data-testid="fiche-job-title" value={draft.job_title} maxLength={120}
            onChange={(e) => set({ job_title: e.target.value })} placeholder="Ex. Formatrice POS" />
        </div>
        <div className="space-y-2">
          <Label>Service</Label>
          <Select value={draft.service_id || NONE} onValueChange={(v) => set({ service_id: v === NONE ? "" : v })}>
            <SelectTrigger data-testid="fiche-service"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Aucun service</SelectItem>
              {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Responsable</Label>
          <CollaboratorPicker
            testid="fiche-manager" value={draft.manager_id || draft.manager_user_id} label={draft.manager_label} allowAccounts
            onChange={(id, name, kind) => set({ manager_id: kind === "collaborator" ? id || "" : "", manager_user_id: kind === "account" ? id : "", manager_label: name || "" })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="fiche-phone">Téléphone professionnel</Label>
          <Input id="fiche-phone" type="tel" inputMode="tel" className="text-base md:text-sm" data-testid="fiche-phone" value={draft.phone}
            onChange={(e) => set({ phone: e.target.value })} placeholder="+221 77 123 45 67" />
        </div>
        <div className="space-y-2">
          <Label>Elle est…</Label>
          <Select value={draft.status} onValueChange={(v) => set({ status: v })}>
            <SelectTrigger data-testid="fiche-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active (travaille déjà avec vous)</SelectItem>
              <SelectItem value="pre_registered">Préinscrite (pas encore commencé)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {draft.status === "active" && (
          <div className="space-y-2">
            <Label htmlFor="fiche-entry">Date d'entrée *</Label>
            <Input id="fiche-entry" type="date" max={today()} className="text-base md:text-sm" data-testid="fiche-entry" value={draft.entry_date} onChange={(e) => set({ entry_date: e.target.value })} />
          </div>
        )}
      </div>
      <p className="text-xs text-slate-500">L'email et le WhatsApp de la fiche reprennent ceux du compte. Vous pourrez compléter le reste depuis la fiche.</p>
    </div>
  );
}
